import React, { useState, useEffect, useMemo } from 'react';
import { Card, Col, Container, Row, Table, Form, InputGroup, Pagination } from 'react-bootstrap';
import BreadCrumb from 'Common/BreadCrumb';
import Swal from 'sweetalert2'; // Import SweetAlert2
import axios from 'axios'; // Import axios
import Chart from 'react-apexcharts'; // Import ApexCharts

const Projects = () => {
  document.title = 'Projects | CITY SERVICE';

  const [isLoading, setIsLoading] = useState(false); // Loading state

  // Dummy data for Rasio Pemenuhan pie chart
  const rasioPemenuhanData = {
    series: [75, 25],
    options: {
      chart: {
        type: 'pie' as const,
        height: 350,
      },
      labels: ['Terpenuhi', 'Belum Terpenuhi'],
      colors: ['#28a745', '#dc3545'],
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 200
          },
          legend: {
            position: 'bottom'
          }
        }
      }],
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
      },
      dataLabels: {
        enabled: true,
        formatter: function (val: number) {
          return Math.round(val) + "%";
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '0%'
          }
        }
      }
    }
  };

  // Dummy data for Card 8 - List Data
  const listData = [
    { id: 1, title: 'Total Projects', value: '24', icon: 'ri-folder-line', color: 'primary' },
    { id: 2, title: 'Active Projects', value: '18', icon: 'ri-play-circle-line', color: 'success' },
    { id: 3, title: 'Completed Projects', value: '6', icon: 'ri-check-circle-line', color: 'info' },
    { id: 4, title: 'Pending Projects', value: '3', icon: 'ri-time-line', color: 'warning' },
  ];
  const [tableData, setTableData] = useState<{ 
    id: number; 
    name: string; 
    latitude: string;
    longtitude: string;
    contract_start: string;
    end_contract: string;
    badan: string;
    company: string;
    leader_pic: string | null;
    created_at: string; 
    updated_at: string; 
  }[]>([]); // Ensure default value is an empty array
  
  // Pagination and Search state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter and paginate data
  const filteredData = useMemo(() => {
    return tableData.filter(project =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.badan.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.company.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [tableData, searchQuery]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle Google Maps link
  const openGoogleMaps = (latitude: string, longitude: string, projectName: string) => {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}&t=m&z=15`;
    window.open(url, '_blank');
  };

  const fetchProjects = async () => {
    setIsLoading(true); // Start loading
    Swal.fire({
      title: 'Loading...',
      html: 'Please wait while we fetch the data.',
      timerProgressBar: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get('https://hris.truest.co.id/api/v1/get-project?company=KAS', {
        headers: {
          Authorization: `Bearer ${token}`, // Add token to Authorization header
        },
      });

      setTableData(response.data.data || []); // Update table data with API response
      Swal.fire({
        icon: 'success',
        title: 'Data Loaded',
        showConfirmButton: false,
        timer: 1000, // Smooth transition after loading
      });
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to fetch projects',
        'error'
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };

  useEffect(() => {
    fetchProjects(); // Fetch projects on component mount
  }, []); // Fetch projects on component mount

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Projects" title="Projects" />

          <Row className="mb-4">
            <Col xl={8} lg={8}>
             <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">Daftar Project</h5>
                    <div className="ms-auto">
                      <InputGroup style={{ width: '300px' }}>
                        <Form.Control
                          type="text"
                          placeholder="Search projects, company, or badan..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                        />
                        <InputGroup.Text>
                          <i className="ri-search-line"></i>
                        </InputGroup.Text>
                      </InputGroup>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Project Name</th>
                        <th>Company/Badan</th>
                        <th>Contract Period</th>
                        <th>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(paginatedData) && paginatedData.length > 0 ? ( // Use paginated data
                        paginatedData.map((project, index) => (
                          <tr key={project.id}>
                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                            <td>{project.name}</td>
                            <td>{project.badan}</td>
                            <td>
                              {project.contract_start} to {project.end_contract}    
                            </td>
                            <td>
                              <button
                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                onClick={() => openGoogleMaps(project.latitude, project.longtitude, project.name)}
                                title={`Open ${project.name} location in Google Maps`}
                              >
                                <i className="ri-map-pin-line"></i>
                                <span>Open Maps</span>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center">
                            {searchQuery ? 'No projects found matching your search' : 'No projects available'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  
                  {/* Pagination and Info */}
                  {filteredData.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
                        {searchQuery && ` (filtered from ${tableData.length} total entries)`}
                      </div>
                      
                      {totalPages > 1 && (
                        <Pagination className="mb-0">
                          <Pagination.First 
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev 
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          />
                          
                          {/* Page numbers */}
                          {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = index + 1;
                            } else if (currentPage <= 3) {
                              pageNum = index + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + index;
                            } else {
                              pageNum = currentPage - 2 + index;
                            }
                            
                            return (
                              <Pagination.Item
                                key={pageNum}
                                active={pageNum === currentPage}
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </Pagination.Item>
                            );
                          })}
                          
                          <Pagination.Next 
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last 
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Card 2 - Rasio Pemenuhan */}
            <Col xl={4} lg={4}>
              <Card>
                <Card.Header>
                  <h4 className="card-title mb-0">Rasio Pemenuhan</h4>
                </Card.Header>
                <Card.Body>
                  <Chart
                    options={rasioPemenuhanData.options}
                    series={rasioPemenuhanData.series}
                    type="pie"
                    height={300}
                  />
                  <div className="mt-3">
                    <div className="row text-center">
                      <div className="col-6">
                        <div className="mt-2">
                          <p className="mb-1 text-truncate fs-13">
                            <i className="mdi mdi-circle text-success me-1"></i>
                            Terpenuhi
                          </p>
                          <h6 className="mb-0">75%</h6>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="mt-2">
                          <p className="mb-1 text-truncate fs-13">
                            <i className="mdi mdi-circle text-danger me-1"></i>
                            Belum Terpenuhi
                          </p>
                          <h6 className="mb-0">25%</h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Projects;
